import {Component, OnInit} from "@angular/core";
import {K2SoftwareSolutionGqlModel, k2SoftwareSolutionsKeys} from "./software-solutions.gql-model";
import {SoftwareSolutionsService} from "./software-solutions.service";
import {handleErrors} from "../misc/handle-errors.decorator";
import {WorkflowService} from "../workflow/workflow.service";
import {WorkflowStep} from "../workflow/workflow-step";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {FilteredList} from "../helpers/filtered-list";
import {DeviceService} from "../device/device.service";
import {DeviceGqlModel, SoftwareDeviceGqlModel} from "../device/device.gql-model";
import {Observable} from "rxjs";
import {debounceTime, distinctUntilChanged, map} from "rxjs/operators";
import {NgbTypeaheadSelectItemEvent} from "@ng-bootstrap/ng-bootstrap";
import {ProjectService} from "../project/project.service";
import {ProjectModel, projectModelFactory} from "../project/project.model";
import {isEqual, omit, cloneDeep} from "lodash";
import {toggleLoading} from "../misc/loading.decorator";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  templateUrl: "./software-solutions-list.template.pug",
  providers: [],
})
@exposeComponentInDebugMode()
export class SoftwareSolutionsListComponent extends WorkflowStep implements OnInit {
  softwareSolutions: FilteredList<K2SoftwareSolutionGqlModel> = new FilteredList<K2SoftwareSolutionGqlModel>({
    searchKeys: ["solutionsPn", "msId", "coId", "licenseNr", "countryId", "dsId"],
    exactMatchSearchKeys: ["id"],
    formKeys: k2SoftwareSolutionsKeys,
    indexField: "index",
  });

  loading: boolean;
  originalSoftwareSolutions: K2SoftwareSolutionGqlModel[] = [];
  searchText = "";
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  error: Error;
  projectId = "";
  projectDetails: ProjectModel = projectModelFactory();
  softwareDeviceList: DeviceGqlModel[] = [];

  countries: any[] = [
    {code: "", name: "Select an option..."},
  ];

  searchHelp: any[] = [
    {property: "Project Name", examples: ["Globex", "\"Massive Dynamic\"", "name:Globex"]},
    {property: "Project Country Name/ID", examples: ["US", "country:US", "country:\"United States\""]},
    {property: "Project Geo Region", examples: ["EMEA", "region:APJ"]},
    {property: "Project ID", examples: ["id:12345678-1234-1234-1234-123456789123"]},
    {property: "Opportunity ID", examples: ["OPP-0000580547", "opportunityId:OPP-0000580547"]},
  ];

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term => (
        term.length < 2 ?
          [] :
          this.softwareDeviceList.filter(
            v =>
              v.productDescription.toLowerCase().indexOf(term.toLowerCase()) > -1 ||
              v.productNumber.toLowerCase().indexOf(term.toLowerCase()) > -1 ||
              v.deviceKey.indexOf(term) > -1,
          ).slice(0, 10)
      )),
    );

  formatter = (result: SoftwareDeviceGqlModel) =>
    `${result.productNumber} / ${result.productDescription} / ${result.deviceKey}`;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    private softwareSolutionsService: SoftwareSolutionsService,
    private projectService: ProjectService,
    private deviceService: DeviceService,
    public workflowService: WorkflowService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.route.queryParams.forEach((value: Params) => {
      this.projectId = value.projectId;
    });
    this.loadData();
  }

  @toggleLoading()
  @handleErrors()
  async loadData() {
    if (!this.projectId) {
      throw new Error("Please go back to the main page and try again.");
    }

    this.projectDetails = await this.projectService.getProjectById(this.projectId).toPromise();

    if (!this.projectDetails.k2Customer) {
      throw new Error("K2 Customer ID is not set for this user, please set a valid value and try again.");
    }

    this.softwareSolutions.items =
      await this.softwareSolutionsService.getSoftwareSolutionsListForCustomer(this.projectDetails.k2Customer);

    this.softwareSolutions.items.forEach((swSolution) => {
      if (swSolution.installDate !== "" && !Number.isNaN(Number(swSolution.installDate))) {
        const date = new Date(parseInt(swSolution.installDate, 10));
        swSolution.installDate = date.toISOString().split("T")[0];
      }

      if (swSolution.solutionsInstallDate !== "" && !Number.isNaN(Number(swSolution.solutionsInstallDate))) {
        const date = new Date(parseInt(swSolution.solutionsInstallDate, 10));
        swSolution.solutionsInstallDate = date.toISOString().split("T")[0];
      }
    });

    this.originalSoftwareSolutions = cloneDeep(this.softwareSolutions.items);

    const count = this.softwareSolutions.items.length;
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }
  }

  @handleErrors()
  async ready() {
    await this.loadData();
  }

  async addSoftwareSolutionItem() {
    this.softwareDeviceList = await this.deviceService.listDevicesForProject(this.projectId, 3);
    const newItem = new K2SoftwareSolutionGqlModel({});
    this.softwareSolutions.insertForAdd(newItem);
    newItem.region = this.projectDetails.geo_region_id;
    newItem.countryId = this.projectDetails.countryId;
    newItem.msId = this.projectDetails.msId;
    newItem.customerId = this.projectDetails.k2Customer;
    newItem.editing = true;
  }

  selectedItem(item: NgbTypeaheadSelectItemEvent, index: number) {
    this.softwareSolutions.items[index].form.patchValue({
      solutionsPn: item.item.productNumber,
      productDescription: item.item.productDescription,
      qtyPurchased: parseInt(item.item.stats.total, 10),
      dsId: item.item.deviceKey,
    });
  }

  @toggleLoading()
  @handleErrors()
  async saveChanges(): Promise<boolean> {
    const k2SoftwareSolutions = [];

    for (const swSolution of this.softwareSolutions.items) {
      const originalSwSolution = this.originalSoftwareSolutions.find(x => x.id === swSolution.id);

      if (
        !originalSwSolution ||
        !isEqual(
          omit(originalSwSolution, ["form", "index"]),
          omit(swSolution, ["form", "index"]),
        )
      ) {
        k2SoftwareSolutions.push(swSolution);
      }
    }

    if (k2SoftwareSolutions.length > 0) {
      await this.softwareSolutionsService.updateSoftwareSolutionsList(
        k2SoftwareSolutions,
        this.projectId,
      );
    }

    await this.loadData();

    return true;
  }
}
